import React from 'react';
import {ReactWizard, WizardPhase} from "../../../components/ReactWizard/ReactWizard";
import Utils from "../../../common/Utils";
import {BusyContent, HeadView, LoadingBars, RegisterComponent} from "../RegisterBase";
import {Alert, Button, NavLink} from "reactstrap";
import FormInput from "../../../components/FormInput/FormInput";
import {UsersEndpoints} from "../../../common/ProxyEndpoints";
import {Form, Formik} from "formik";
import * as Yup from 'yup';
import DocumentTitle from 'react-document-title';
import AppVariables from '../../../common/AppVariables.js';
import _, {ActivationFormSchema} from "../../ActivateAccount";
import {getCrops} from "../../../data/crops";
import PlatformRender from "../../../components/PlatformRender/PlatformRender";
import {DevicePlatforms, Gender} from "../../../common/Constants";
import OtpInput from 'react-code-input';
import CodeInputView from "../../../components/CodeInputView/CodeInputView";

const RegisterHead = (props) => {
    return <HeadView title="Register" message="Farmer" info={props.phaseInfo}/>;
};

const AccountInfoSchema = Yup.object().shape({
    username: Yup.string()
        .required('Please enter a valid username')
        .min(AppVariables.MinUsernameLength)
        .matches(/[a-z]+[a-z0-9@_.\-]+$/,"Username cannot be numeric or contain special characters and whitespaces except \"@\", \".\", \"_\"")
        .matches(/[a-z0-9]$/,"Username can only end with a letter or number")
        .label("Username"),
    password: Yup.string().required('Please enter your password').min(AppVariables.MinPasswordLength).label("Password"),
    password_confirm: Yup.string().required('Confirm password is required').oneOf([Yup.ref('password'),null],"Passwords do not match").label("Confirm Password")
});

const PersonalInfoSchema = Yup.object().shape({
    name: Yup.string()
        .trim()
        .required('Please enter a valid name')
        .matches(/[a-zA-Z]{1,}/,"Invalid name. Cannot begin with a number or special characters")
        .matches(/[a-zA-Z0-9]$/,"Name cannot end with symbols or any special character")
        .label('Name'),
    email: Yup.string().trim().email().label('Email'),
    phone: Yup.string()
        .required('Enter a valid phone number')
        .test('phone','Must be a valid phone number', val => Utils.validatePhoneNumber(val))
        .label('Phone')
});

const FarmInfoSchema = Yup.object().shape({
    location: Yup.object().nullable().required('Please enter your farm location').label('Location'),
    hectares: Yup.number().nullable().label("Hectares"),
    crop: Yup.string().trim().nullable().label("Crop")
});


class RegisterFarmer extends RegisterComponent{

    constructor(props){
        super(props, 'register_farmer');

        this.setPhaseInfo({

            account_info: {
                index: 0,
                getForm: () => this._accountInfoForm,
                validate: this.onVerifyAccountInfo.bind(this)
            },

            personal_info: {
                index: 1,
                getForm: () => this._personalInfoForm,
                validate: this.onVerifyPersonalInfo.bind(this)
            },

            farm_info: {
                index: 2,
                getForm: () => this._farmInfoForm,
                validate: this.onVerifyFarmInfo.bind(this)
            },

            create_account: {
                index: 3
            },

            activate_account: {
                index: 4,
                getForm: () => this._activateForm,
                validate: this.onActivateAccount.bind(this)
            }

        });

    }

    onVerifyFarmInfo(){

        const { location , hectares , crop } = this._farmInfoForm.getFormikContext().values;

        const farmInfo = {
            farm_location: location.text,
            farm_location_lat: location.lat,
            farm_location_long: location.lng,
            hectares,
            crop
        };

        this.persistState('farmInfo', farmInfo);

        return Promise.resolve(true);
    }

    onVerifyAccountInfo(){

        const { password_confirm , ...accountInfo } = this._accountInfoForm.getFormikContext().values;
        this.persistState('accountInfo',accountInfo);
        return this.processValidation( UsersEndpoints.registerFarmerAccount(true,false,accountInfo) );
    }

    onVerifyPersonalInfo(){

        const { phone , email , name , sex } = this._personalInfoForm.getFormikContext().values;

        let farmerEmail = email ? email : undefined;
        let farmerPhone = Utils.fixPhone(phone);
        const personalInfo = {
            phone: farmerPhone,
            email: farmerEmail,
            farmer:{
                name,
                email: farmerEmail,
                phone: farmerPhone,
                hectares: 1,
                sex: Number(sex)
            }
        };

        this.persistState('personalInfo',personalInfo);
        return this.processValidation(UsersEndpoints.registerFarmerAccount(true,false,personalInfo));
    }

    onCreateAccount(){

        const { farmer, ...personalInfo } = this.getStateValue('personalInfo');

        const payload = {
            ...this.getStateValue('accountInfo'),
            ...personalInfo,
            farmer: {
              ...farmer,
              ...this.getStateValue('farmInfo')
            }
        };

        return this.processRegister(UsersEndpoints.registerFarmerAccount(false,true,payload));
    }

    onActivateAccount(){
        const { activation_code } = this._activateForm.getFormikContext().values;
        const { username , password } = this.getStateValue('accountInfo');
        return this.processAccountActivation(activation_code,username,password);
    }

    wizardRender(){

        return (<DocumentTitle title={"Register - Farmer"}>
                <ReactWizard ref={(wizard) => this._wizard = wizard} sessionKey="register_farmer_wz_session">

                    {/*Account info phase*/}
                    <WizardPhase name="Account Info">

                        <Formik validationSchema={AccountInfoSchema}
                                initialValues={{ username: '', password: '', password_confirm: ''  }}
                                onSubmit={async (values, { setSubmitting }) => {
                                    await this.moveNextPhase();
                                    setSubmitting(false);
                                }}
                                ref={r => this._accountInfoForm = r}>

                            {({ isValid }) => (<Form className="card-block p-4 animated fadeIn">

                                <RegisterHead phaseInfo="ACCOUNT INFO"/>

                                <div>

                                    {/*Progress indicator here*/}
                                    <FormInput  name="username"
                                                type="text"
                                                title="Username"
                                                disabled={this.state.isBusy}
                                                required={true}
                                                icon="fa fa-user"
                                                placeholder="Enter your account username here..."
                                                showPrompt={true}/>

                                    <FormInput  name="password"
                                                type="password"
                                                required={true}
                                                title="Password"
                                                disabled={this.state.isBusy}
                                                icon="fa fa-lock"
                                                placeholder="Enter your password here..."
                                                showPrompt={true}/>

                                    <FormInput  name="password_confirm"
                                                type="password"
                                                required={true}
                                                disabled={this.state.isBusy}
                                                title="Confirm password"
                                                icon="fa fa-lock"
                                                placeholder="Confirm your password..."/>

                                </div>

                                {this.renderCurrentError()}

                                {this.state.isBusy && <LoadingBars/>}

                                <Button type="submit" color="green" disabled={!isValid || this.state.isBusy} block>Continue <i className="icon-arrow-right"></i></Button>

                                <div className="text-center" style={{'marginTop': '10px'}}>
                                    <NavLink href="#" onClick={Utils.navigateLink.bind(this,'/login')}> <i className="icon-arrow-left"></i> <b>Already have an account</b></NavLink>
                                </div>

                            </Form>)}
                        </Formik>

                    </WizardPhase>


                    {/*Personal info phase*/}
                    <WizardPhase name="Personal Info">

                        <Formik validationSchema={PersonalInfoSchema}
                                initialValues={{ name: '', phone: '', email: '' , location: null , sex: Gender.Male }}
                                onSubmit={async (values,{ setSubmitting }) => {
                                    await this.moveNextPhase();
                                    setSubmitting(false);
                                }}
                                ref={r => this._personalInfoForm = r}>

                            {({ isValid , isSubmitting })=> (<Form className="card-block p-4 animated fadeIn">

                                <RegisterHead phaseInfo="PERSONAL INFO"/>

                                <div>

                                    {/*Progress indicator here*/}
                                    <FormInput  name="name"
                                                type="text"
                                                title="Full Name"
                                                required={true}
                                                icon="fa fa-user"
                                                disabled={this.state.isBusy}
                                                showPrompt={true}
                                                placeholder="Enter your full name here..."/>

                                    <FormInput  name="phone"
                                                type="phone"
                                                title="Phone"
                                                required={true}
                                                disabled={this.state.isBusy}
                                                icon="fa fa-phone"
                                                showPrompt={true}
                                                placeholder="Enter your phone number here..."/>

                                                <hr/>

                                    <FormInput  name="email"
                                                type="email"
                                                title="Email"
                                                icon="fa fa-envelope"
                                                disabled={this.state.isBusy}
                                                required={false}
                                                showPrompt={true}
                                                placeholder="Enter your email here..."/>

                                    <FormInput  name="sex"
                                                type="select"
                                                title="Gender"
                                                disabled={this.state.isBusy}
                                                required={true}
                                                showPrompt={true}
                                                items={[
                                                    { value : Gender.Male , text: 'Male' },
                                                    { value : Gender.Female , text: 'Female'}
                                                ]}/>

                                </div>

                                {this.renderCurrentError()}

                                {this.state.isBusy && <LoadingBars/>}

                                <Button type="submit" color="green" disabled={!isValid || this.state.isBusy} block>Proceed <i className="icon-arrow-right"></i></Button>

                                <div className="text-center" style={{'marginTop': '10px'}}>
                                    <NavLink href="#" onClick={this.movePreviousPhase}><i className="icon-arrow-left"></i> <b>Back to account info</b></NavLink>
                                </div>

                            </Form>)}

                        </Formik>


                    </WizardPhase>

                    {/*Farm info phase*/}
                    <WizardPhase name="Farm Info">

                        <Formik validationSchema={FarmInfoSchema}
                                initialValues={{  location: null , hectares: '' , crop: '' }}
                                onSubmit={async (values,{ setSubmitting }) => {
                                    await this.moveNextPhase();
                                    setSubmitting(false);
                                }}
                                ref={r => this._farmInfoForm = r}>

                            {({ isValid , isSubmitting })=> (<Form className="card-block p-4 animated fadeIn">

                                <RegisterHead phaseInfo="FARM DETAILS"/>

                                <div>

                                    <FormInput  name="location"
                                                type="location"
                                                title="Location"
                                                disabled={this.state.isBusy}
                                                required={true}
                                                showPrompt={true}
                                                icon="fa fa-map-marker"
                                                placeholder="Enter your farm's location"/>

                                    <FormInput  name="hectares"
                                                type="number"
                                                title="Farm Size (hectares)"
                                                icon="fa fa-dot-circle-o"
                                                disabled={this.state.isBusy}
                                                required={true}
                                                showPrompt={true}
                                                placeholder="Enter your farm size"/>

                                    <FormInput  name="crop"
                                                type="dropdown"
                                                title="Crops"
                                                disabled={this.state.isBusy}
                                                required={false}
                                                showPrompt={true}
                                                items={getCrops().map(c => ({ value: c, text: c }))}
                                                multi={true}
                                                simple={true}
                                                placeholder="Which crops do you grow"/>

                                </div>

                                {this.renderCurrentError()}

                                {this.state.isBusy && <LoadingBars/>}

                                <Button type="submit" color="green" disabled={!isValid || this.state.isBusy} block>Register <i className="fa fa-plus"/></Button>

                                <div className="text-center" style={{'marginTop': '10px'}}>
                                    <NavLink href="#" onClick={this.movePreviousPhase}><i className="icon-arrow-left"></i> <b>Back to personal info</b></NavLink>
                                </div>

                            </Form>)}

                        </Formik>


                    </WizardPhase>

                    {/* Create account phase */}
                    <WizardPhase name="RegisterFarmer" onActivate={() => this.onCreateAccount() }>

                        <div className="card-block p-4 animated fadeIn" >
                            <BusyContent title="Processing account" message="Please hold on while we prepare your account..." />
                        </div>

                    </WizardPhase>

                    {/*Activate user account*/}
                    <WizardPhase name="Activate Account">

                        <div className="card-block p-4 animated fadeIn">

                            <Formik initialValues={{ activation_code: '' }}
                                    validationSchema={ActivationFormSchema}
                                    onSubmit={async (values ,{ setSubmitting }) => {
                                        await this.moveNextPhase();
                                        setSubmitting(false);
                                    }}
                                    ref={r => this._activateForm = r}>

                                {({ isValid  }) => {

                                    return (<Form>


                                        <div className="text-center">
                                            <PlatformRender platform={DevicePlatforms.Desktop}>
                                                <h3>Activate Account</h3>
                                            </PlatformRender>

                                            <PlatformRender platform={DevicePlatforms.Mobile}>
                                                <h4>Activate Account</h4>
                                            </PlatformRender>
                                            <p>Just one more step to go...</p>

                                            <hr/>

                                            <Alert color="info"><b>Hint:</b> You've been sent <b>activation code</b>. Enter the code below to proceed.</Alert>

                                        </div>


                                        <CodeInputView name={"activation_code"} size={0.8} className="mb-2"/>

                                        <div>
                                            {this.state.isBusy && <LoadingBars/>}
                                        </div>

                                        {this.renderCurrentError()}

                                        <Button type="submit" color="green" disabled={!isValid || this.state.isBusy} block>
                                            Activate Account <i className="fa fa-check"></i>
                                        </Button>

                                        <div className="text-center" style={{'marginTop': '10px'}}>
                                            <NavLink href="#" onClick={Utils.navigateLink.bind(this,'/login')}> <i className="icon-arrow-left"></i> <b>Back to Login</b></NavLink>
                                        </div>

                                    </Form>)
                                }}
                            </Formik>


                        </div>

                    </WizardPhase>

                </ReactWizard>
            </DocumentTitle>
        )

    };

}

export default  RegisterFarmer;



// WEBPACK FOOTER //
// ./src/views/Register/Farmer/RegisterFarmer.js